import type { VoiceOption } from '@/api/client'

interface VoicePickerProps {
  voices: VoiceOption[];
  value: string;
  onChange: (voiceId: string) => void;
}

/**
 * Which voice interviews the candidate. Rendered on the pre-interview
 * surfaces (practice preview, invite preview) — the choice is sent to
 * `getToken` and the worker builds the matching TTS.
 */
export function VoicePicker({ voices, value, onChange }: VoicePickerProps) {
  // A default-only catalog means no alternative voice is configured
  // (YarnGPT key unset) — there is nothing to choose, so render nothing.
  if (voices.length <= 1) return null

  // One effective selection drives both the pressed state and the hint: a
  // value outside the catalog (the catalog hook demotes stale ids, but this
  // component stays correct on its own) reads as the first voice instead of
  // showing no selection next to the wrong description.
  const current = voices.some(v => v.id === value) ? value : voices[0].id
  const selected = voices.find(v => v.id === current) ?? voices[0]

  return (
    <div className="field">
      <span className="field-label">Interviewer voice</span>
      <div className="wchips" role="group" aria-label="Interviewer voice">
        {voices.map(voice => (
          <button
            key={voice.id}
            type="button"
            className="chip"
            aria-pressed={voice.id === current}
            onClick={() => onChange(voice.id)}
          >
            {voice.label}
          </button>
        ))}
      </div>
      {/* The description is the only signal of what the picked voice sounds
          like, and focus stays on the clicked chip — announce its change. */}
      <span className="hint" aria-live="polite">{selected.description}</span>
    </div>
  )
}
